import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, ArrowRight, CaretLeft, CaretRight, ArrowsOutSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Before/After Slider Component
const BeforeAfterSlider = ({ beforeImage, afterImage, title }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    if (isDragging.current) {
      handleMove(e.clientX);
    }
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-80 overflow-hidden rounded-sm cursor-ew-resize select-none"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUp}
      data-testid={`before-after-slider-${title}`}
    >
      {/* After Image (Background) */}
      <img
        src={afterImage}
        alt="Après"
        className="absolute inset-0 w-full h-full object-cover"
      />
      
      {/* Before Image (Clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={beforeImage}
          alt="Avant"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: containerRef.current ? containerRef.current.offsetWidth : '100%' }}
        />
      </div>
      
      {/* Slider Handle */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
          <div className="flex items-center gap-0.5">
            <CaretLeft size={14} weight="bold" className="text-[#333333]" />
            <CaretRight size={14} weight="bold" className="text-[#333333]" />
          </div>
        </div>
      </div>
      
      {/* Labels */}
      <div className="absolute top-4 left-4 bg-black/70 text-white text-xs font-bold px-3 py-1 rounded">
        AVANT
      </div>
      <div className="absolute top-4 right-4 bg-[#E74C3C] text-white text-xs font-bold px-3 py-1 rounded">
        APRÈS
      </div>
    </div>
  );
};

// Image Gallery Component
const ImageGallery = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative w-full h-80 overflow-hidden rounded-sm group" data-testid={`gallery-${title}`}>
      <img
        src={images[currentIndex]}
        alt={`${title} - Image ${currentIndex + 1}`}
        className="w-full h-full object-cover transition-transform duration-500"
      />
      
      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`gallery-prev-${title}`}
          >
            <CaretLeft size={20} weight="bold" className="text-[#333333]" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`gallery-next-${title}`}
          >
            <CaretRight size={20} weight="bold" className="text-[#333333]" />
          </button>
        </>
      )}
      
      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? "bg-white w-6" : "bg-white/50"
              }`}
            />
          ))}
        </div>
      )}
      
      {/* Image Counter */}
      <div className="absolute top-4 right-4 bg-black/70 text-white text-xs font-bold px-3 py-1 rounded">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
};

export default function PortfolioPage() {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const response = await axios.get(`${API}/portfolio`);
        setPortfolio(response.data);
      } catch (error) {
        console.error("Error fetching portfolio:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPortfolio();
  }, []);

  const renderProjectCard = (item, index) => {
    const isBeforeAfter = item.type === "before-after";
    const isGallery = item.type === "gallery";
    const mainImage = isBeforeAfter ? item.after_image : (item.images ? item.images[0] : item.after_image);

    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className="bg-white rounded-sm shadow-lg overflow-hidden"
        data-testid={`portfolio-item-${item.id}`}
      >
        {/* Image/Slider Section */}
        {isBeforeAfter ? (
          <BeforeAfterSlider
            beforeImage={item.before_image}
            afterImage={item.after_image}
            title={item.title}
          />
        ) : isGallery ? (
          <ImageGallery images={item.images} title={item.title} />
        ) : (
          <div className="relative h-80 overflow-hidden">
            <img
              src={mainImage}
              alt={item.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-[#333333] mb-2">{item.title}</h3>
          <p className="text-[#64748B] text-sm mb-4">{item.description}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-[#64748B]">
              <span className="flex items-center gap-1">
                <MapPin size={16} className="text-[#1B93A4]" />
                {item.location}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={16} className="text-[#1B93A4]" />
                {item.year}
              </span>
            </div>
            {isBeforeAfter && (
              <span className="bg-[#E74C3C]/10 text-[#E74C3C] text-xs font-bold px-3 py-1 rounded-full">
                Avant / Après
              </span>
            )}
            {isGallery && item.images && item.images.length > 1 && (
              <span className="bg-[#1B93A4]/10 text-[#1B93A4] text-xs font-bold px-3 py-1 rounded-full">
                {item.images.length} photos
              </span>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="pt-20" data-testid="portfolio-page">
      {/* Hero Section */}
      <section className="bg-[#F8F9FA] py-20" data-testid="portfolio-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Portfolio
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#333333] mt-3">
              Nos Réalisations
            </h1>
            <p className="text-[#64748B] text-lg mt-6">
              Découvrez une sélection de nos projets récents. Chaque réalisation témoigne 
              de notre savoir-faire et de notre engagement envers la qualité.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-16 bg-white" data-testid="portfolio-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {portfolio.map((item, index) => renderProjectCard(item, index))}
          </div>

          {portfolio.length === 0 && !loading && (
            <div className="text-center py-16">
              <p className="text-[#64748B] text-lg">
                Aucune réalisation disponible pour le moment.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#333333]" data-testid="portfolio-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Votre Projet Pourrait Être le Prochain
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Contactez-nous pour discuter de votre projet et obtenir un devis gratuit personnalisé.
            </p>
            <Link to="/contact">
              <Button
                className="bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide px-10 py-6 rounded-sm text-base"
                data-testid="portfolio-final-cta"
              >
                Demander un Devis Gratuit
                <ArrowRight className="ml-2" size={20} weight="bold" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
